import React, { useState, useEffect, useCallback } from 'react';
import AppShell from '../components/layout/AppShell';
import { LoadingSpinner } from '../components/layout/SharedComponents';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import api from '../services/api';
import { toast } from 'sonner';
import {
  TrendingDown, DollarSign, BarChart3, Download, FileText, Users, Tag, Calendar,
} from 'lucide-react';
import { SortableTableHeader, useSortState, sortData } from '../components/SortableTableHeader';
import { useAuth } from '../contexts/AuthContext';

export default function SavingsReportPage() {
  const { systemSettings } = useAuth();
  const tz = systemSettings?.timezone || 'America/Los_Angeles';
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('overview');
  const [evtSort, handleEvtSort] = useSortState('saving_amount', 'desc');
  const [suppSort, handleSuppSort] = useSortState('saving_amount', 'desc');
  const [catSort, handleCatSort] = useSortState('saving_amount', 'desc');
  const cur = systemSettings?.system_currency || 'USD';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/savings/dashboard');
      setData(r.data);
    } catch { toast.error('Failed to load savings data'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const fmtMoney = (v) => `$${(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const fmtPct = (v) => `${(v || 0).toFixed(1)}%`;

  // ── Export functions ────────────────────────────────────────────────────────
  const exportExcel = async () => {
    try {
      const XLSX = await import('xlsx');
      const wb = XLSX.utils.book_new();

      // Summary sheet
      const summary = [
        ['Procurement Savings Report', '', '', ''],
        ['Generated', new Date().toLocaleString(), '', ''],
        ['', '', '', ''],
        ['Period', 'Saving Amount', 'Baseline Total', 'Saving %'],
        ['This Month', data.totals.this_month.saving_amount, data.totals.this_month.total_baseline, data.totals.this_month.saving_pct + '%'],
        ['Year to Date', data.totals.year_to_date.saving_amount, data.totals.year_to_date.total_baseline, data.totals.year_to_date.saving_pct + '%'],
        ['All Time', data.totals.all_time.saving_amount, data.totals.all_time.total_baseline, data.totals.all_time.saving_pct + '%'],
        ['Cost Avoidance (All Time)', data.totals.all_time.cost_avoidance, '', ''],
      ];
      const ws1 = XLSX.utils.aoa_to_sheet(summary);
      XLSX.utils.book_append_sheet(wb, ws1, 'Summary');

      // Events sheet
      if (data.events?.length) {
        const evHeaders = ['Event Ref', 'Type', 'Title', 'Category', 'Currency', 'Baseline', 'Awarded', 'Saving', 'Saving %', 'Calculated'];
        const evRows = data.events.map(e => [e.event_ref, e.event_type, e.title, e.category, e.currency, e.total_baseline, e.total_awarded, e.saving_amount, e.saving_pct + '%', e.calculated_at]);
        const ws2 = XLSX.utils.aoa_to_sheet([evHeaders, ...evRows]);
        XLSX.utils.book_append_sheet(wb, ws2, 'By Event');
      }

      // Supplier sheet
      if (data.by_supplier?.length) {
        const supHeaders = ['Supplier', 'Baseline', 'Awarded', 'Saving', 'Saving %', 'Events'];
        const supRows = data.by_supplier.map(s => [s.supplier_code, s.total_baseline, s.total_awarded, s.saving_amount, s.saving_pct + '%', s.event_count]);
        const ws3 = XLSX.utils.aoa_to_sheet([supHeaders, ...supRows]);
        XLSX.utils.book_append_sheet(wb, ws3, 'By Supplier');
      }

      // Category sheet
      if (data.by_category?.length) {
        const catHeaders = ['Category', 'Baseline', 'Awarded', 'Saving', 'Saving %', 'Events'];
        const catRows = data.by_category.map(c => [c.category, c.total_baseline, c.total_awarded, c.saving_amount, c.saving_pct + '%', c.event_count]);
        const ws4 = XLSX.utils.aoa_to_sheet([catHeaders, ...catRows]);
        XLSX.utils.book_append_sheet(wb, ws4, 'By Category');
      }

      XLSX.writeFile(wb, `Savings_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Report exported to Excel');
    } catch (e) { toast.error('Export failed: ' + e.message); }
  };

  const exportPDF = () => {
    // Use browser print with specific styling
    const printContent = document.getElementById('savings-report-content');
    if (!printContent) { toast.error('Nothing to print'); return; }
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`<!DOCTYPE html><html><head><title>Procurement Savings Report</title>
      <style>
        body { font-family: Arial, sans-serif; margin: 20px; color: #333; }
        h1 { font-size: 20px; color: #1e3a5f; margin-bottom: 4px; }
        h2 { font-size: 16px; color: #1e3a5f; margin-top: 24px; border-bottom: 2px solid #e5e7eb; padding-bottom: 4px; }
        .subtitle { font-size: 12px; color: #666; margin-bottom: 20px; }
        .kpi-row { display: flex; gap: 16px; margin: 16px 0; }
        .kpi { border: 1px solid #e5e7eb; border-radius: 8px; padding: 12px 16px; text-align: center; flex: 1; }
        .kpi .value { font-size: 24px; font-weight: 700; }
        .kpi .label { font-size: 11px; color: #666; }
        .green { color: #059669; }
        table { width: 100%; border-collapse: collapse; margin: 12px 0; font-size: 12px; }
        th { background: #f8f9fa; padding: 8px; text-align: left; font-weight: 600; border-bottom: 2px solid #e5e7eb; }
        td { padding: 6px 8px; border-bottom: 1px solid #f0f0f0; }
        .right { text-align: right; }
        .mono { font-family: monospace; }
        @media print { body { margin: 0; } }
      </style></head><body>
      <h1>Procurement Savings Report</h1>
      <p class="subtitle">Generated: ${new Date().toLocaleString()} | ${data?.event_count || 0} events analysed</p>
      ${printContent.innerHTML}
    </body></html>`);
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); }, 500);
    toast.success('Opening print/PDF dialog...');
  };

  if (loading) return <AppShell><div className="flex items-center justify-center h-96"><LoadingSpinner /></div></AppShell>;
  if (!data) return <AppShell><p className="text-center text-slate-400 py-12">Unable to load savings data.</p></AppShell>;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'events', label: 'By Event', icon: FileText },
    { id: 'suppliers', label: 'By Supplier', icon: Users },
    { id: 'categories', label: 'By Category', icon: Tag },
  ];

  return (
    <AppShell>
      <div className="p-4 sm:p-6 space-y-4" data-testid="savings-report-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
              <TrendingDown className="w-6 h-6 text-emerald-600" /> Procurement Savings
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">{data.event_count} sourcing event(s) analysed</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={exportExcel} data-testid="export-excel-btn">
              <Download className="w-3.5 h-3.5 mr-1" /> Export Excel
            </Button>
            <Button variant="outline" size="sm" onClick={exportPDF} data-testid="export-pdf-btn">
              <FileText className="w-3.5 h-3.5 mr-1" /> Save as PDF
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b gap-0">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors
                ${tab === t.id ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              data-testid={`savings-tab-${t.id}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* Content — wrapped in printable div */}
        <div id="savings-report-content">
          {tab === 'overview' && (
            <div className="space-y-4">
              {/* KPI Cards */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="border rounded-xl p-4 text-center bg-white">
                  <DollarSign className="w-5 h-5 text-emerald-600 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-emerald-700">{fmtMoney(data.totals.this_month.saving_amount)}</p>
                  <p className="text-xs text-slate-500">Savings This Month</p>
                  {data.totals.this_month.saving_pct > 0 && <p className="text-[10px] text-emerald-600 font-medium">{fmtPct(data.totals.this_month.saving_pct)} vs baseline</p>}
                </div>
                <div className="border rounded-xl p-4 text-center bg-white">
                  <Calendar className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-blue-700">{fmtMoney(data.totals.year_to_date.saving_amount)}</p>
                  <p className="text-xs text-slate-500">Savings Year to Date</p>
                  {data.totals.year_to_date.saving_pct > 0 && <p className="text-[10px] text-blue-600 font-medium">{fmtPct(data.totals.year_to_date.saving_pct)} vs baseline</p>}
                </div>
                <div className="border rounded-xl p-4 text-center bg-white">
                  <TrendingDown className="w-5 h-5 text-indigo-600 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-indigo-700">{fmtMoney(data.totals.all_time.saving_amount)}</p>
                  <p className="text-xs text-slate-500">Savings All Time</p>
                  {data.totals.all_time.saving_pct > 0 && <p className="text-[10px] text-indigo-600 font-medium">{fmtPct(data.totals.all_time.saving_pct)} vs baseline</p>}
                </div>
                <div className="border rounded-xl p-4 text-center bg-white">
                  <BarChart3 className="w-5 h-5 text-amber-600 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-amber-700">{fmtMoney(data.totals.all_time.cost_avoidance)}</p>
                  <p className="text-xs text-slate-500">Cost Avoidance</p>
                </div>
              </div>

              {/* Top events */}
              <div className="border rounded-xl bg-white overflow-hidden">
                <div className="px-4 py-3 border-b bg-slate-50">
                  <h2 className="font-bold text-sm">Top Savings by Event</h2>
                </div>
                <table className="w-full text-sm">
                  <thead><tr className="border-b bg-slate-50/50">
                    <SortableTableHeader label="Event" field="event_ref" sortState={evtSort} onSort={handleEvtSort} />
                    <SortableTableHeader label="Type" field="event_type" sortState={evtSort} onSort={handleEvtSort} />
                    <SortableTableHeader label="Title" field="title" sortState={evtSort} onSort={handleEvtSort} />
                    <SortableTableHeader label="Baseline" field="total_baseline" sortState={evtSort} onSort={handleEvtSort} className="text-right" />
                    <SortableTableHeader label="Awarded" field="total_awarded" sortState={evtSort} onSort={handleEvtSort} className="text-right" />
                    <SortableTableHeader label="Saving" field="saving_amount" sortState={evtSort} onSort={handleEvtSort} className="text-right" />
                    <SortableTableHeader label="%" field="saving_pct" sortState={evtSort} onSort={handleEvtSort} className="text-right" />
                  </tr></thead>
                  <tbody>
                    {sortData((data.events || []).slice(0, 10), evtSort.field, evtSort.dir).map((e, i) => (
                      <tr key={i} className="border-b hover:bg-slate-50">
                        <td className="px-4 py-2 font-semibold text-indigo-700 text-xs">{e.event_ref}</td>
                        <td className="px-4 py-2"><Badge className="text-[10px]">{e.event_type}</Badge></td>
                        <td className="px-4 py-2 text-xs truncate max-w-[200px]">{e.title}</td>
                        <td className="px-4 py-2 text-right text-xs font-mono">{fmtMoney(e.total_baseline)}</td>
                        <td className="px-4 py-2 text-right text-xs font-mono">{fmtMoney(e.total_awarded)}</td>
                        <td className="px-4 py-2 text-right text-xs font-mono font-semibold text-emerald-700">{fmtMoney(e.saving_amount)}</td>
                        <td className="px-4 py-2 text-right text-xs font-semibold">{fmtPct(e.saving_pct)}</td>
                      </tr>
                    ))}
                    {(!data.events || data.events.length === 0) && (
                      <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400 text-xs">No savings data available. Calculate savings on individual sourcing events first.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {tab === 'events' && (
            <div className="border rounded-xl bg-white overflow-hidden">
              <table className="w-full text-sm">
                <thead><tr className="border-b bg-slate-50">
                  <SortableTableHeader label="Event Ref" field="event_ref" sortState={evtSort} onSort={handleEvtSort} />
                  <SortableTableHeader label="Type" field="event_type" sortState={evtSort} onSort={handleEvtSort} />
                  <SortableTableHeader label="Title" field="title" sortState={evtSort} onSort={handleEvtSort} />
                  <SortableTableHeader label="Category" field="category" sortState={evtSort} onSort={handleEvtSort} />
                  <SortableTableHeader label="Currency" field="currency" sortState={evtSort} onSort={handleEvtSort} />
                  <SortableTableHeader label="Baseline" field="total_baseline" sortState={evtSort} onSort={handleEvtSort} className="text-right" />
                  <SortableTableHeader label="Awarded" field="total_awarded" sortState={evtSort} onSort={handleEvtSort} className="text-right" />
                  <SortableTableHeader label="Saving" field="saving_amount" sortState={evtSort} onSort={handleEvtSort} className="text-right" />
                  <SortableTableHeader label="%" field="saving_pct" sortState={evtSort} onSort={handleEvtSort} className="text-right" />
                  <SortableTableHeader label="Calculated" field="calculated_at" sortState={evtSort} onSort={handleEvtSort} />
                </tr></thead>
                <tbody>
                  {sortData((data.events || []), evtSort.field, evtSort.dir).map((e, i) => (
                    <tr key={i} className="border-b hover:bg-slate-50">
                      <td className="px-4 py-2 font-semibold text-indigo-700 text-xs">{e.event_ref}</td>
                      <td className="px-4 py-2"><Badge className="text-[10px]">{e.event_type}</Badge></td>
                      <td className="px-4 py-2 text-xs truncate max-w-[200px]">{e.title}</td>
                      <td className="px-4 py-2 text-xs">{e.category || '—'}</td>
                      <td className="px-4 py-2 text-xs">{e.currency}</td>
                      <td className="px-4 py-2 text-right text-xs font-mono">{fmtMoney(e.total_baseline)}</td>
                      <td className="px-4 py-2 text-right text-xs font-mono">{fmtMoney(e.total_awarded)}</td>
                      <td className="px-4 py-2 text-right text-xs font-mono font-semibold text-emerald-700">{fmtMoney(e.saving_amount)}</td>
                      <td className="px-4 py-2 text-right text-xs">{fmtPct(e.saving_pct)}</td>
                      <td className="px-4 py-2 text-xs text-slate-400">{e.calculated_at ? new Date(e.calculated_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: tz }) : ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {tab === 'suppliers' && (
            <div className="border rounded-xl bg-white overflow-hidden">
              <table className="w-full text-sm">
                <thead><tr className="border-b bg-slate-50">
                  <SortableTableHeader label="Supplier" field="supplier_code" sortState={suppSort} onSort={handleSuppSort} />
                  <SortableTableHeader label={`Total Baseline (${cur})`} field="total_baseline" sortState={suppSort} onSort={handleSuppSort} className="text-right" />
                  <SortableTableHeader label={`Total Awarded (${cur})`} field="total_awarded" sortState={suppSort} onSort={handleSuppSort} className="text-right" />
                  <SortableTableHeader label="Saving" field="saving_amount" sortState={suppSort} onSort={handleSuppSort} className="text-right" />
                  <SortableTableHeader label="%" field="saving_pct" sortState={suppSort} onSort={handleSuppSort} className="text-right" />
                  <SortableTableHeader label="Events" field="event_count" sortState={suppSort} onSort={handleSuppSort} className="text-right" />
                </tr></thead>
                <tbody>
                  {sortData((data.by_supplier || []), suppSort.field, suppSort.dir).map((s, i) => (
                    <tr key={i} className="border-b hover:bg-slate-50">
                      <td className="px-4 py-2 font-medium text-xs">{s.supplier_code}</td>
                      <td className="px-4 py-2 text-right text-xs font-mono">{fmtMoney(s.total_baseline)}</td>
                      <td className="px-4 py-2 text-right text-xs font-mono">{fmtMoney(s.total_awarded)}</td>
                      <td className="px-4 py-2 text-right text-xs font-mono font-semibold text-emerald-700">{fmtMoney(s.saving_amount)}</td>
                      <td className="px-4 py-2 text-right text-xs">{fmtPct(s.saving_pct)}</td>
                      <td className="px-4 py-2 text-right text-xs">{s.event_count}</td>
                    </tr>
                  ))}
                  {(!data.by_supplier || data.by_supplier.length === 0) && (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400 text-xs">No supplier savings data.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {tab === 'categories' && (
            <div className="border rounded-xl bg-white overflow-hidden">
              <table className="w-full text-sm">
                <thead><tr className="border-b bg-slate-50">
                  <SortableTableHeader label="Category" field="category" sortState={catSort} onSort={handleCatSort} />
                  <SortableTableHeader label={`Total Baseline (${cur})`} field="total_baseline" sortState={catSort} onSort={handleCatSort} className="text-right" />
                  <SortableTableHeader label="Total Awarded" field="total_awarded" sortState={catSort} onSort={handleCatSort} className="text-right" />
                  <SortableTableHeader label="Saving" field="saving_amount" sortState={catSort} onSort={handleCatSort} className="text-right" />
                  <SortableTableHeader label="%" field="saving_pct" sortState={catSort} onSort={handleCatSort} className="text-right" />
                  <SortableTableHeader label="Events" field="event_count" sortState={catSort} onSort={handleCatSort} className="text-right" />
                </tr></thead>
                <tbody>
                  {sortData((data.by_category || []), catSort.field, catSort.dir).map((c, i) => (
                    <tr key={i} className="border-b hover:bg-slate-50">
                      <td className="px-4 py-2 font-medium text-xs">{c.category}</td>
                      <td className="px-4 py-2 text-right text-xs font-mono">{fmtMoney(c.total_baseline)}</td>
                      <td className="px-4 py-2 text-right text-xs font-mono">{fmtMoney(c.total_awarded)}</td>
                      <td className="px-4 py-2 text-right text-xs font-mono font-semibold text-emerald-700">{fmtMoney(c.saving_amount)}</td>
                      <td className="px-4 py-2 text-right text-xs">{fmtPct(c.saving_pct)}</td>
                      <td className="px-4 py-2 text-right text-xs">{c.event_count}</td>
                    </tr>
                  ))}
                  {(!data.by_category || data.by_category.length === 0) && (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400 text-xs">No category savings data.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
